import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiLock } from "react-icons/fi";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { AttachmentGrid } from "../../../common/comms/AttachmentGrid";
import { normalizeAttachments } from "../../../utils/commsAttachments";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { formatClock } from "./ticketRowMeta";

/** `HH:MM` in the viewer's locale, with a "Today" prefix for today's messages. */
const stamp = (iso) => {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const time = then.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const isToday = then.toDateString() === new Date().toDateString();
  return isToday ? `Today ${time}` : `${then.toLocaleDateString()} ${time}`;
};

/** Plain text of a message for the collapsed one-liner. */
const excerpt = (message) => {
  const text = (message?.content || "").trim();
  if (text) return text;
  const attachments = normalizeAttachments(message);
  return attachments.length ? `${attachments.length} attachment(s)` : "(no content)";
};

/**
 * A run of consecutive agent replies, collapsed to one line.
 *
 * The design collapses these because an agent re-opening a ticket is looking
 * for what the *customer* last said, not re-reading their own team's replies —
 * but the content is one click away, never hidden.
 */
const CollapsedRun = ({ messages, nameFor }) => {
  const [open, setOpen] = useState(false);
  const last = messages[messages.length - 1];

  if (open) {
    return messages.map((m) => (
      <MessageBlock key={m.id || m.seq} message={m} nameFor={nameFor} />
    ));
  }
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="flex w-full items-center gap-2 border-b border-gray-100 px-3.5 py-2 text-left transition-colors hover:bg-gray-50"
    >
      <AvatarRing name={nameFor(last)} colorIndex={0} size="xs" />
      <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
        {nameFor(last)} — “{excerpt(last)}”
      </span>
      {messages.length > 1 && (
        <span className="shrink-0 rounded-full bg-gray-100 px-1.5 text-[10px] font-semibold text-gray-500">
          {messages.length}
        </span>
      )}
      <span className="shrink-0 font-mono text-[10px] text-gray-400">
        {stamp(last?.created_at)}
      </span>
      <FiChevronDown aria-hidden="true" size={13} className="shrink-0 text-gray-400" />
    </button>
  );
};

/**
 * One expanded message — the email-style block: who, when, the body, and any
 * attachments.
 */
const MessageBlock = ({ message, nameFor, awaitingFor = null }) => {
  const attachments = useMemo(() => normalizeAttachments(message), [message]);
  const internal = Boolean(message.coach_only);
  const system = message.message_type === 2;
  const name = nameFor(message);

  if (system) {
    return (
      <p className="m-0 border-b border-gray-100 bg-rail-bg px-3.5 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {message.content}
        <span className="ml-2 font-mono font-normal normal-case tracking-normal text-gray-400">
          {stamp(message.created_at)}
        </span>
      </p>
    );
  }

  if (internal) {
    return (
      <div className="border-b border-gray-100 border-l-[3px] border-l-internal bg-internal-bg px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          <FiLock aria-hidden="true" size={10} className="text-internal-text" />
          <span className="text-[9px] font-bold tracking-[0.08em] text-internal-text/80">
            INTERNAL NOTE
          </span>
          <span className="text-xs font-bold text-internal-text">{name}</span>
          <span className="ml-auto font-mono text-[10px] text-internal-text/70">
            {stamp(message.created_at)}
          </span>
        </div>
        <p className="m-0 mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-internal-text">
          {message.content}
        </p>
        {attachments.length > 0 && (
          <div className="mt-2">
            <AttachmentGrid attachments={attachments} />
          </div>
        )}
      </div>
    );
  }

  const isCustomer = message.message_type === 0;

  return (
    <div className="border-b border-gray-100 px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <AvatarRing
          name={name}
          colorIndex={isCustomer ? 3 : 0}
          size="sm"
          className="!h-6 !w-6"
        />
        <span className="text-xs font-bold text-gray-900">{name}</span>
        <span className="text-[10px] text-gray-400">
          {isCustomer ? "customer" : "agent"}
        </span>
        {awaitingFor && (
          <span className="rounded-sm bg-danger-bg px-1.5 py-px text-[9px] font-bold text-danger-dark">
            AWAITING REPLY {awaitingFor}
          </span>
        )}
        <span className="ml-auto font-mono text-[10px] text-gray-400">
          {stamp(message.created_at)}
        </span>
      </div>
      {message.content && (
        <p className="m-0 mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-gray-800">
          {message.content}
        </p>
      )}
      {attachments.length > 0 && (
        <div className="mt-2">
          <AttachmentGrid attachments={attachments} />
        </div>
      )}
    </div>
  );
};

/**
 * The customer's CSAT, pinned to the end of the thread.
 *
 * It belongs in the conversation rather than the rail because it is the last
 * thing the customer said — and the comment often explains a rating the number
 * alone would misrepresent ("sorted it, but took all day" is a 4 that is really
 * a complaint about the first-reply clock).
 */
const CsatBlock = ({ csat }) => {
  // Never trust the caller for the one value that gets rendered as a child:
  // the detail payload's `csat` is a record, not a number, and putting an
  // object here would take the whole pane down.
  const rating = Number(csat?.rating);
  if (!Number.isFinite(rating)) return null;
  const filled = Math.max(0, Math.min(5, Math.round(rating)));

  return (
    <div className="border-b border-gray-100 bg-rail-bg px-3.5 py-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-gray-900">
          {csat.name || "The customer"} rated this ticket
        </span>
        <span className="ml-auto font-mono text-[10px] text-gray-400">
          {stamp(csat.at)}
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <span aria-hidden="true" className="text-sm tracking-widest text-internal">
          {"★".repeat(filled)}
          <span className="text-gray-300">{"★".repeat(5 - filled)}</span>
        </span>
        <span className="text-xs font-bold text-gray-700">{rating} / 5</span>
      </div>
      {csat.comment && (
        <p className="m-0 mt-1.5 text-[13px] italic leading-relaxed text-gray-700">
          “{csat.comment}”
        </p>
      )}
    </div>
  );
};

/**
 * The ticket conversation, rendered email-style rather than as chat bubbles.
 *
 * A support thread is read differently from a coaching chat: it's a record
 * being audited — who said what, when, and how long the customer waited — so
 * each message is a full-width block with an explicit header, internal notes
 * are visually separated rather than merely tinted, and runs of agent replies
 * collapse so the customer's last word is always the thing in view.
 * `Messages/Thread.jsx` keeps its bubbles; nothing here touches it.
 *
 * @param {Object} props
 * @param {Array<Object>} props.messages
 * @param {Array<Object>} props.participants
 * @param {boolean} props.forbidden - Room refused with 403 (not picked up yet).
 * @param {boolean} [props.hasMore]
 * @param {boolean} [props.loadingOlder]
 * @param {() => void} [props.onLoadOlder]
 * @param {React.ReactNode} [props.pickUpPrompt] - Rendered in place of the
 *   thread when `forbidden`.
 * @param {{ rating: number, comment?: string, name?: string, at?: string }|null}
 *   [props.csat] - The customer's rating, pinned to the end of the thread.
 */
export function TicketThread({
  messages,
  participants,
  forbidden,
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
  pickUpPrompt,
  csat = null,
}) {
  const { getClientName, getCoachName } = useCommsIdentity();
  const bottomRef = useRef(null);

  const nameFor = useMemo(() => {
    const byId = new Map(
      (participants || []).map((p) => [
        String(p.resolved_user_id ?? p.user_id),
        p.name,
      ])
    );
    return (message) => {
      const id = String(message?.resolved_user_id ?? message?.sender_id ?? "");
      if (message?.ai_generated) return "Flex AI";
      return (
        message?.name ||
        byId.get(id) ||
        (message?.message_type === 0 ? getClientName(id) : getCoachName(id)) ||
        (message?.message_type === 0 ? "Customer" : "Agent")
      );
    };
  }, [participants, getClientName, getCoachName]);

  // Group consecutive agent replies so a long back-and-forth stays scannable.
  // The most recent run is never collapsed — that's the part being acted on.
  const blocks = useMemo(() => {
    const out = [];
    for (const message of messages) {
      const collapsible = message.message_type === 1 && !message.coach_only;
      const prev = out[out.length - 1];
      if (collapsible && prev?.kind === "run") {
        prev.messages.push(message);
      } else if (collapsible) {
        out.push({ kind: "run", messages: [message] });
      } else {
        out.push({ kind: "single", message });
      }
    }
    // Expand the trailing run — and any run of one, which reads worse collapsed
    // than shown.
    return out.map((block, i) =>
      block.kind === "run" && (i === out.length - 1 || block.messages.length === 1)
        ? { kind: "expanded", messages: block.messages }
        : block
    );
  }, [messages]);

  // How long the customer has been waiting on the last unanswered message.
  const awaitingFor = useMemo(() => {
    const lastCustomer = [...messages].reverse().find((m) => m.message_type === 0);
    if (!lastCustomer) return null;
    const isLast = messages[messages.length - 1] === lastCustomer;
    if (!isLast || !lastCustomer.created_at) return null;
    return formatClock(Date.now() - Date.parse(lastCustomer.created_at));
  }, [messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  if (forbidden) {
    return <div className="flex min-h-0 flex-1 items-center justify-center">{pickUpPrompt}</div>;
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {hasMore && (
        <div className="p-2 text-center">
          <button
            type="button"
            onClick={onLoadOlder}
            disabled={loadingOlder}
            className="rounded-md border border-gray-200 px-3 py-1 text-[11px] font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingOlder ? "Loading…" : "Load earlier messages"}
          </button>
        </div>
      )}

      {messages.length === 0 ? (
        <p className="px-4 py-10 text-center text-xs text-gray-400">
          No messages on this ticket yet.
        </p>
      ) : (
        blocks.map((block, i) =>
          block.kind === "run" ? (
            <CollapsedRun key={`run-${i}`} messages={block.messages} nameFor={nameFor} />
          ) : block.kind === "expanded" ? (
            block.messages.map((m) => (
              <MessageBlock key={m.id || m.seq} message={m} nameFor={nameFor} />
            ))
          ) : (
            <MessageBlock
              key={block.message.id || block.message.seq}
              message={block.message}
              nameFor={nameFor}
              awaitingFor={
                block.message === messages[messages.length - 1] ? awaitingFor : null
              }
            />
          )
        )
      )}

      {csat && <CsatBlock csat={csat} />}
      <div ref={bottomRef} />
    </div>
  );
}

export default TicketThread;
